import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_CHARS = 50000;
const WARN_CHARS = 45000;

const channelDefs = [
  { value: 'email', icon: '📧' },
  { value: 'sms', icon: '💬' },
  { value: 'whatsapp', icon: '📱' },
  { value: 'telegram', icon: '✈️' },
  { value: 'messenger', icon: '💭' },
  { value: 'instagram', icon: '📷' },
  { value: 'leboncoin', icon: '🏷️' },
  { value: 'marketplace', icon: '🛒' },
  { value: 'vinted', icon: '👗' },
  { value: 'phone', icon: '📞' },
  { value: 'website', icon: '🌐' },
  { value: 'other', icon: '❓' },
];

const channelGroupDefs = [
  {
    key: 'messaging',
    options: ['email', 'sms', 'whatsapp', 'telegram', 'messenger', 'instagram'],
  },
  {
    key: 'marketplaces',
    options: ['leboncoin', 'marketplace', 'vinted'],
  },
  {
    key: 'other',
    options: ['phone', 'website', 'other'],
  },
];

export default function EmailInput({ onSubmit, isLoading }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('paste'); // 'paste' or 'upload'
  const [emailContent, setEmailContent] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPseudo, setSenderPseudo] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [channel, setChannel] = useState('email');
  const [userContext, setUserContext] = useState('');
  const [fileName, setFileName] = useState('');
  const fileRef = useRef(null);

  // Show relevant fields based on channel
  const showEmailField = ['email', 'other'].includes(channel);
  const showPhoneField = ['sms', 'whatsapp', 'telegram', 'phone'].includes(channel);
  const showPseudoField = true;  // Always show — scammers often use pseudonyms on all channels

  const buildData = () => {
    const extras = { channel };
    if (senderEmail.trim()) extras.sender_email_hint = senderEmail.trim();
    if (senderPseudo.trim()) extras.sender_pseudo = senderPseudo.trim();
    if (senderPhone.trim()) extras.sender_phone = senderPhone.trim();
    if (userContext.trim()) extras.user_context = userContext.trim();
    return extras;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (mode === 'upload' && fileRef.current?.files?.[0]) {
      const formData = new FormData();
      formData.append('email_file', fileRef.current.files[0]);
      formData.append('submission_type', 'upload_eml');
      const extras = buildData();
      Object.entries(extras).forEach(([k, v]) => formData.append(k, v));
      onSubmit(formData);
    } else if (emailContent.trim()) {
      const data = { raw_email: emailContent, submission_type: 'paste_raw', ...buildData() };
      onSubmit(data);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setFileName(file.name);
  };

  const channelObj = channelDefs.find((c) => c.value === channel);

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-4 justify-center">
        <button
          type="button"
          onClick={() => setMode('paste')}
          aria-label={t('emailInput.pasteMode')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'paste'
              ? 'bg-safe/20 text-safe border border-safe/30'
              : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white'
          }`}
        >
          {t('emailInput.pasteMode')}
        </button>
        <button
          type="button"
          onClick={() => setMode('upload')}
          aria-label={t('emailInput.uploadMode')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'upload'
              ? 'bg-safe/20 text-safe border border-safe/30'
              : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white'
          }`}
        >
          {t('emailInput.uploadMode')}
        </button>
      </div>

      {mode === 'paste' ? (
        <div className="relative">
          <label htmlFor="email-content" className="sr-only">Contenu du message</label>
          <textarea
            id="email-content"
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            placeholder={t('emailInput.placeholder')}
            className="input-field w-full h-48 resize-none font-mono text-sm"
            disabled={isLoading}
            maxLength={MAX_CHARS}
            aria-describedby="email-content-helper"
          />
          <p id="email-content-helper" className="sr-only">
            Collez le contenu brut du message ou email suspect. Maximum {MAX_CHARS} {t('emailInput.chars')}.
          </p>
          <div
            className={`absolute bottom-3 right-3 text-xs ${
              emailContent.length > WARN_CHARS ? 'text-red-500 font-medium' : 'text-gray-500'
            }`}
          >
            {emailContent.length} / {MAX_CHARS} {t('emailInput.chars')}
          </div>
        </div>
      ) : (
        <div
          className="glass-card p-8 text-center cursor-pointer hover:border-safe/30 transition-all"
          onClick={() => fileRef.current?.click()}
        >
          <label htmlFor="email-file-upload" className="sr-only">Fichier email</label>
          <input
            id="email-file-upload"
            ref={fileRef}
            type="file"
            accept=".eml,.msg,.txt"
            onChange={handleFileChange}
            className="hidden"
          />
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
            aria-label="Telecharger un fichier email"
            role="img"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          {fileName ? (
            <p className="text-safe font-medium">{fileName}</p>
          ) : (
            <>
              <p className="text-gray-400">{t('emailInput.uploadPrompt')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('emailInput.maxSize')}</p>
            </>
          )}
        </div>
      )}

      {/* Channel selector */}
      <div className="mt-4">
        <label htmlFor="channel-select" className="block text-gray-400 text-sm mb-2">{t('emailInput.channelLabel')}</label>

        {/* Mobile: dropdown */}
        <div className="md:hidden">
          <select
            id="channel-select"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            disabled={isLoading}
            className="input-field w-full text-sm"
          >
            {channelGroupDefs.map((group) => (
              <optgroup key={group.key} label={t('channels.' + group.key)}>
                {group.options.map((val) => {
                  const ch = channelDefs.find((c) => c.value === val);
                  return (
                    <option key={val} value={val}>
                      {ch.icon} {t('channels.' + val)}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Desktop: button grid */}
        <div className="hidden md:flex flex-wrap gap-2">
          {channelDefs.map((ch) => (
            <button
              key={ch.value}
              type="button"
              onClick={() => setChannel(ch.value)}
              disabled={isLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                channel === ch.value
                  ? 'bg-safe/20 text-safe border-safe/30'
                  : 'bg-dark-surface text-gray-400 border-dark-border hover:text-white hover:border-gray-500'
              }`}
            >
              <span className="mr-1">{ch.icon}</span>
              {t('channels.' + ch.value)}
            </button>
          ))}
        </div>
      </div>

      {/* Sender identification fields -- adapts to channel */}
      <div className="mt-4 space-y-3">
        <p className="text-gray-400 text-sm">{t('emailInput.senderInfo')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {showEmailField && (
            <div>
              <label htmlFor="sender-email" className="sr-only">Email de l'arnaqueur</label>
              <input
                id="sender-email"
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder={t('emailInput.emailPlaceholder')}
                className="input-field w-full text-sm"
                disabled={isLoading}
              />
            </div>
          )}

          {showPhoneField && (
            <div>
              <label htmlFor="sender-phone" className="sr-only">Telephone de l'arnaqueur</label>
              <input
                id="sender-phone"
                type="tel"
                value={senderPhone}
                onChange={(e) => setSenderPhone(e.target.value)}
                placeholder={t('emailInput.phonePlaceholder')}
                className="input-field w-full text-sm"
                disabled={isLoading}
              />
            </div>
          )}

          {showPseudoField && (
            <div>
              <label htmlFor="sender-pseudo" className="sr-only">Pseudo de l'arnaqueur</label>
              <input
                id="sender-pseudo"
                type="text"
                value={senderPseudo}
                onChange={(e) => setSenderPseudo(e.target.value)}
                placeholder={t('emailInput.pseudoPlaceholder')}
                className="input-field w-full text-sm"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Always show email for non-email channels as optional */}
          {!showEmailField && (
            <div>
              <label htmlFor="sender-email-optional" className="sr-only">Email associe (optionnel)</label>
              <input
                id="sender-email-optional"
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder={t('emailInput.optionalEmail')}
                className="input-field w-full text-sm"
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        <p className="text-gray-600 text-xs">
          {t('emailInput.senderHelp')}
        </p>
      </div>

      {/* Optional context from user */}
      <div className="mt-4">
        <label htmlFor="user-context" className="block text-gray-400 text-sm mb-2">
          {t('emailInput.contextLabel')} <span className="text-gray-600">{t('emailInput.contextOptional')}</span>
        </label>
        <textarea
          id="user-context"
          value={userContext}
          onChange={(e) => setUserContext(e.target.value)}
          placeholder={t('emailInput.contextPlaceholder')}
          className="input-field w-full text-sm resize-none"
          rows={2}
          maxLength={1000}
          disabled={isLoading}
        />
        <p className="text-gray-600 text-xs mt-1">
          {t('emailInput.contextHelp')}
        </p>
      </div>

      {/* Hidden honeypot field */}
      <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

      <button
        type="submit"
        disabled={isLoading || (mode === 'paste' && !emailContent.trim()) || (mode === 'upload' && !fileName)}
        className="btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t('emailInput.analyzing')}
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            {t('emailInput.analyze')}
          </>
        )}
      </button>
    </form>
  );
}
